// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Orbit Event Utility Functions
 * Pure functions for handling orbit entry, fuel refuel offers, and exploration offers
 */

import GameConfig from '../../config/GameConfig';

/**
 * Calculate refuel costs and options
 * @param {number} currentFuel - Current fuel percentage
 * @param {number} maxFuel - Maximum fuel capacity
 * @param {number} credits - Available credits
 * @returns {Object} Refuel calculation results
 */
export const calculateRefuelOptions = (currentFuel, maxFuel, credits) => {
  const fuelNeeded = maxFuel - currentFuel;
  const fullRefuelCost = Math.ceil(fuelNeeded * GameConfig.credits.refuelCostPerPercent);
  const canAffordFull = credits >= fullRefuelCost;
  const canAffordPartial = credits > 0;
  const partialFuelAmount = Math.floor(credits / GameConfig.credits.refuelCostPerPercent);
  const partialCost = Math.floor(partialFuelAmount * GameConfig.credits.refuelCostPerPercent);
  
  return {
    fuelNeeded,
    fullRefuelCost,
    canAffordFull,
    canAffordPartial,
    partialFuelAmount,
    partialCost
  };
};

/**
 * Check if refuel offer should be shown
 * @param {Object} activeEvent - Current active event
 * @param {number} currentFuel - Current fuel percentage
 * @param {number} maxFuel - Maximum fuel capacity
 * @returns {boolean} Whether to show refuel offer
 */
export const shouldShowRefuelOffer = (activeEvent, currentFuel, maxFuel) => {
  return !activeEvent && currentFuel < maxFuel;
};

/**
 * Build refuel event actions
 * @param {Object} refuelOptions - Refuel calculation results
 * @param {Function} onFullRefuel - Callback for full refuel
 * @param {Function} onPartialRefuel - Callback for partial refuel
 * @param {Function} onDecline - Callback for decline
 * @returns {Array} Array of action objects
 */
export const buildRefuelActions = (refuelOptions, onFullRefuel, onPartialRefuel, onDecline) => {
  const { canAffordFull, canAffordPartial, fullRefuelCost, partialFuelAmount, partialCost } = refuelOptions;
  const actions = [];
  
  if (canAffordFull) {
    actions.push({
      label: `REFUEL TO ${refuelOptions.fuelNeeded + refuelOptions.fuelNeeded}% (-${fullRefuelCost} CREDITS)`,
      feedback: '⛽ REFUELING IN PROGRESS...',
      effect: onFullRefuel
    });
  } else if (canAffordPartial && partialFuelAmount >= 1) {
    actions.push({
      label: `PARTIAL REFUEL +${partialFuelAmount}% (-${partialCost} CREDITS)`,
      feedback: '⛽ PARTIAL REFUELING IN PROGRESS...',
      effect: onPartialRefuel
    });
  }
  
  actions.push({
    label: canAffordFull || canAffordPartial ? 'DECLINE REFUEL' : 'DECLINE (INSUFFICIENT CREDITS)',
    feedback: canAffordFull || canAffordPartial ? '➡️ REFUEL DECLINED - Proceeding with current fuel' : '⚠️ CANNOT REFUEL - Not enough credits',
    effect: onDecline
  });
  
  return actions;
};

/**
 * Create refuel offer event object
 * @param {Object} refuelOptions - Refuel calculation results
 * @param {number} currentFuel - Current fuel percentage
 * @param {number} maxFuel - Maximum fuel capacity
 * @param {number} credits - Available credits
 * @param {Array} actions - Action objects
 * @returns {Object} Event object for refuel offer
 */
export const createRefuelOfferEvent = (refuelOptions, currentFuel, maxFuel, credits, actions) => {
  const { fuelNeeded, fullRefuelCost, canAffordFull, canAffordPartial, partialFuelAmount, partialCost } = refuelOptions;
  
  return {
    type: 'FUEL_REFUEL_OFFER',
    title: '⛽ REFUELING AVAILABLE',
    message: `Station refueling services available.\n\nCurrent Fuel: ${Math.round(currentFuel)}%\nMax Capacity: ${maxFuel}%\nFuel Needed: ${Math.round(fuelNeeded)}%\n\nFull Refuel Cost: ${fullRefuelCost} credits\nYour Credits: ${credits}${!canAffordFull && canAffordPartial ? `\n\n⚠️ Can afford ${partialFuelAmount}% fuel for ${partialCost} credits` : !canAffordPartial ? '\n\n⚠️ INSUFFICIENT CREDITS' : ''}`,
    color: canAffordFull ? '#00ff88' : canAffordPartial ? '#ffaa00' : '#ff4444',
    icon: '⛽',
    actions
  };
};

/**
 * Check if exploration offer should be shown
 * @param {Object} activeEvent - Current active event
 * @param {Object} currentLocation - Current location object
 * @returns {boolean} Whether to show exploration offer
 */
export const shouldShowExplorationOffer = (activeEvent, currentLocation) => {
  const hasExploration = currentLocation && currentLocation.services && currentLocation.services.includes('exploration');
  return !activeEvent && hasExploration;
};

/**
 * Create exploration offer event object
 * @param {Object} currentLocation - Current location object
 * @param {Array} actions - Action objects
 * @returns {Object} Event object for exploration offer
 */
export const createExplorationOfferEvent = (currentLocation, actions) => {
  return {
    type: 'EXPLORATION_OFFER',
    title: '🌍 PLANETARY EXPLORATION',
    message: `${currentLocation.name} is available for surface exploration.\n\nExploration missions can yield valuable resources or encounter hazards.\n\nWould you like to launch an exploration shuttle?`,
    color: '#ff00ff',
    icon: '🌍',
    actions
  };
};

/**
 * Build exploration event actions
 * @param {Function} onAccept - Callback for accepting exploration
 * @param {Function} onDecline - Callback for declining exploration
 * @returns {Array} Array of action objects
 */
export const buildExplorationActions = (onAccept, onDecline) => {
  return [
    {
      label: 'LAUNCH EXPLORATION SHUTTLE',
      feedback: '🚀 LAUNCHING SHUTTLE...',
      effect: onAccept
    },
    {
      label: 'DECLINE EXPLORATION',
      feedback: '➡️ EXPLORATION DECLINED',
      effect: onDecline
    }
  ];
};

/**
 * Create updated location object for orbit entry
 * @param {Object} selectedSystem - Selected system object
 * @returns {Object} Updated location object
 */
export const createOrbitLocation = (selectedSystem) => {
  return {
    ...selectedSystem,
    x: undefined,
    y: undefined,
    z: undefined,
    inTransit: false
  };
};

/**
 * Check if weapon reload offer should be shown
 * @param {Object} activeEvent - Current active event
 * @param {Object} currentLocation - Current location object
 * @returns {boolean} Whether to show weapon reload offer
 */
export const shouldShowWeaponReloadOffer = (activeEvent, currentLocation) => {
  const hasWeaponReload = currentLocation && currentLocation.services && currentLocation.services.includes('weaponReload');
  return !activeEvent && hasWeaponReload;
};

/**
 * Check if upgrade shop offer should be shown
 * @param {Object} activeEvent - Current active event
 * @param {Object} currentLocation - Current location object
 * @returns {boolean} Whether to show upgrade shop offer
 */
export const shouldShowUpgradeShopOffer = (activeEvent, currentLocation) => {
  const hasUpgrades = currentLocation && currentLocation.services && currentLocation.services.includes('upgrades');
  return !activeEvent && hasUpgrades;
};

/**
 * Create weapon reload offer event object
 * @param {Object} currentLocation - Current location object
 * @param {Array} actions - Action objects
 * @returns {Object} Event object for weapon reload offer
 */
export const createWeaponReloadOfferEvent = (currentLocation, actions) => {
  return {
    type: 'WEAPON_RELOAD_OFFER',
    title: '⚔️ WEAPON RELOAD AVAILABLE',
    message: `${currentLocation.name} offers weapon reloading services.\n\nReload your weapons to full capacity?`,
    color: '#ff8800',
    icon: '⚔️',
    actions
  };
};

/**
 * Create upgrade shop offer event object
 * @param {Object} currentLocation - Current location object
 * @param {Array} actions - Action objects
 * @returns {Object} Event object for upgrade shop offer
 */
export const createUpgradeShopOfferEvent = (currentLocation, actions) => {
  return {
    type: 'UPGRADE_SHOP_OFFER',
    title: '🔧 UPGRADE SHOP AVAILABLE',
    message: `${currentLocation.name} has a ship upgrade facility.\n\nWould you like to browse available upgrades?`,
    color: '#00ffff',
    icon: '🔧',
    actions
  };
};

/**
 * Build weapon reload event actions
 * @param {Function} onAccept - Callback for accepting reload
 * @param {Function} onDecline - Callback for declining reload
 * @returns {Array} Array of action objects
 */
export const buildWeaponReloadActions = (onAccept, onDecline) => {
  return [
    {
      label: 'OPEN WEAPON RELOAD',
      feedback: '⚔️ ACCESSING WEAPON SYSTEMS...',
      effect: onAccept
    },
    {
      label: 'DECLINE RELOAD',
      feedback: '➡️ WEAPON RELOAD DECLINED',
      effect: onDecline
    }
  ];
};

/**
 * Build upgrade shop event actions
 * @param {Function} onAccept - Callback for accepting shop
 * @param {Function} onDecline - Callback for declining shop
 * @returns {Array} Array of action objects
 */
export const buildUpgradeShopActions = (onAccept, onDecline) => {
  return [
    {
      label: 'BROWSE UPGRADES',
      feedback: '🔧 ACCESSING UPGRADE SHOP...',
      effect: onAccept
    },
    {
      label: 'DECLINE UPGRADES',
      feedback: '➡️ UPGRADE SHOP DECLINED',
      effect: onDecline
    }
  ];
};
